import { FC, useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, Divider, Grid, IconButton, Tooltip, Typography } from "@material-ui/core";
import { Add, Delete, ExpandMore } from "@material-ui/icons";
import { FieldArray, useFormikContext } from "formik";
import SelectField from "src/components/ui-components/SelectField";
import InputField from "src/components/ui-components/InputField";
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import * as fasIcons from '@fortawesome/free-solid-svg-icons';

library.add(fas);

type FormValues = {
  API: {
    WebMethodKey: string;
    Fields?: {
      FieldName?: string | null;
      DisplayName?: string | null;
      Type?: string | null;
      Format?: string | null;
      APIResponse?: string | null;
      MapField?: string | null;
      Icon?: string | null;
      SortOrder: number | null;
    }[];
    WidgetSort?: {
      SortField: string;
      SortType: string;
    }[];
    WidgetFieldGrouping?: {
      GroupByField: string | null;
      GroupByAggField: string | null;
    }[];
    WidgetFieldFilter?: {
      FilterField: string | null;
      FilterValue: string | null;
    }[];
    // idx: number;
    // GroupByField?: string | null;
    // GroupByAggField?: string | null;
  }[];
};

const defaultField = {
  FieldName: null,
  DisplayName: null,
  Type: null,
  Format: null,
  APIResponse: null,
  MapField: null,
  Icon: 'Blank',
  SortOrder: 1
};

type APIConfigurationsCardProps = {
  webMethodsList: any[],
  setAreDeleteChanges: any;
  FieldTypeOptions: any[];
  FieldFormatOptions: any[];
  APIResponseParams: any[];
  selectedWidgetType: string | null;
};

const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

const iconOptions = Object.keys(fasIcons)
  .filter(icon => icon !== 'fas' && icon !== 'prefix')
  .map(icon => ({ label: icon, value: icon }));

iconOptions.unshift({ label: 'Empty', value: 'Empty' });

const APIConfigurationsCard: FC<APIConfigurationsCardProps> = ({ webMethodsList, setAreDeleteChanges, FieldTypeOptions, FieldFormatOptions, APIResponseParams, selectedWidgetType }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [apiDialogOpen, setApiDialogOpen] = useState<boolean>(false);
  const [fieldDialogOpen, setFieldDialogOpen] = useState<boolean>(false);
  const [apiIndex, setApiIndex] = useState<number | null>(null);
  const [fieldIndex, setFieldIndex] = useState<number | null>(null);
  const [selectedIcons, setSelectedIcons] = useState({});

  const handleApiDelete = (idx) => {
    setApiDialogOpen(true);
    setApiIndex(idx);
  };

  const handleFieldDelete = (apiIndex, fieldIndex) => {
    setFieldDialogOpen(true);
    setApiIndex(apiIndex);
    setFieldIndex(fieldIndex);
  };

  const handleDeleteApiConfirmation = () => {
    const newApiList = [...values.API];
    newApiList.splice(apiIndex, 1);
    setFieldValue("API", newApiList);
    setApiDialogOpen(false);
    setApiIndex(null);
    setAreDeleteChanges(true);
  };

  const handleDeleteFieldConfirmation = () => {
    const newApiList = [...values.API];
    newApiList[apiIndex].Fields = newApiList[apiIndex].Fields.filter((_, mappingIdx) => mappingIdx !== fieldIndex);
    setFieldValue("API", newApiList);
    setFieldDialogOpen(false);
    setAreDeleteChanges(true);
  };

  const handleIconChange = (newValue, idx, fieldIdx) => {
    if (newValue) {
      setSelectedIcons((prevState) => ({
        ...prevState,
        [`${idx}-${fieldIdx}`]: fasIcons[newValue.value],
      }));
      setFieldValue(`API.${idx}.Fields.${fieldIdx}.Icon`, newValue.value);
    } else {
      setSelectedIcons((prevState) => ({
        ...prevState,
        [`${idx}-${fieldIdx}`]: null,
      }));
      setFieldValue(`API.${idx}.Fields.${fieldIdx}.Icon`, null);
    }
  };

  const handleFieldTypeChange = (newValue, idx, fieldIdx) => {
    if (newValue) {
      setFieldValue(`API.${idx}.Fields.${fieldIdx}.Type`, newValue.fieldDataTypeMasterKey);
    } else {
      setFieldValue(`API.${idx}.Fields.${fieldIdx}.Type`, null);
    }
    setFieldValue(`API.${idx}.Fields.${fieldIdx}.Format`, null);
  };

  useEffect(() => {
    const initialIcons = {};
    values.API.forEach((api, idx) => {
      api.Fields?.forEach((field, fieldIdx) => {
        initialIcons[`${idx}-${fieldIdx}`] = fas[field.Icon];
      });
    });
    setSelectedIcons(initialIcons);
  }, [values.API]);

  return (
    <>
      <Card>
        <CardContent>
          <Box>
            <Grid container spacing={2}>
              <Typography sx={{ marginLeft: "2%", marginTop: "2%", marginBottom: "1%" }} variant="h5">
                API Configurations
              </Typography>
            </Grid>
            <Grid container xs={12} sm={12} md={12} lg={12} xl={12} sx={{ marginLeft: "1%", marginBottom: "2%" }}>
              <FieldArray name="API">
                {({ push }) => (
                  <>
                    {values?.API?.map((api, idx) => (
                      <Accordion key={`${api.WebMethodKey || idx}-${idx}`} defaultExpanded sx={{ width: "98%" }}>
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          aria-controls="field-content"
                          id="field-header"
                          sx={{ alignItems: "center" }}
                        >
                          {api.WebMethodKey && api.WebMethodKey !== '' ? (
                            <Tooltip title={webMethodsList?.find(x => x.webMethodKey === api.WebMethodKey)?.webMethodName || 'API'}>
                              <Typography sx={{ fontSize: '16px !important' }} variant="h6">
                                {webMethodsList?.find(x => x.webMethodKey === api.WebMethodKey)?.webMethodName &&
                                  webMethodsList?.find(x => x.webMethodKey === api.WebMethodKey)?.webMethodName.length > 20 ?
                                  truncateText(webMethodsList?.find(x => x.webMethodKey === api.WebMethodKey)?.webMethodName, 20) :
                                  webMethodsList?.find(x => x.webMethodKey === api.WebMethodKey)?.webMethodName}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '16px !important' }} variant="h5">
                              API
                            </Typography>
                          )}
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box >
                            <Grid container spacing={2}>
                              <Grid item xs={6} sm={6} md={6} lg={6} xl={6} sx={{ marginTop: "1%" }}>
                                <SelectField
                                  data={webMethodsList.filter(item =>
                                    // Filter out the options already selected in the previous dropdowns
                                    !values.API.some((selectedApi, selectedIdx) => selectedApi.WebMethodKey === item.webMethodKey && selectedIdx !== idx)
                                  )}
                                  dataTextField={"webMethodName"}
                                  dataValueField={"webMethodKey"}
                                  name={`API.${idx}.WebMethodKey`}
                                  label="API *"
                                />
                              </Grid>
                              <Grid item xs={6} sm={6} md={6} lg={6} xl={6} sx={{ marginTop: "1%" }}>
                                {values.API.length > 1 && (
                                  <IconButton
                                    onClick={() => handleApiDelete(idx)}
                                  >
                                    <Delete />
                                  </IconButton>
                                )}
                                {idx === values.API.length - 1 && (
                                  <IconButton
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      push({
                                        WebMethodKey: null,
                                        Fields: [{
                                          FieldName: null,
                                          DisplayName: null,
                                          Type: null,
                                          Format: null,
                                          APIResponse: null,
                                          MapField: null,
                                          Icon: null,
                                          SortOrder: 1
                                        }],
                                        WidgetSort: [{
                                          SortType: "",
                                          SortField: ""
                                        }],
                                        WidgetFieldGrouping: [{
                                          GroupByField: "",
                                          GroupByAggField: ""
                                        }],
                                        WidgetFieldFilter: [{
                                          FilterField: "",
                                          FilterValue: ""
                                        }],
                                      });
                                    }}
                                    aria-label="Add"
                                  >
                                    <Add />
                                  </IconButton>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ marginTop: "1%" }}>
                                <Divider />
                                <Typography variant="h5" sx={{ mt: "3px" }}>Widget Sort</Typography>
                                <FieldArray name={`API.${idx}.WidgetSort`}>
                                  {({ push: pushSort, remove: removeSort }) => (
                                    <>
                                      {api.WidgetSort?.map((sort, sortIdx) => (
                                        <Grid container spacing={2} key={sortIdx} sx={{ mt: "3px" }}>
                                          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                                            <SelectField
                                              name={`API.${idx}.WidgetSort.${sortIdx}.SortField`}
                                              data={APIResponseParams.filter(x => x.webMethodKey === api.WebMethodKey) || []}
                                              dataTextField={"parameterTagDisplayName"}
                                              dataValueField={"parameterTagDisplayName"}
                                              label="Sort Field *"
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                                            <SelectField
                                              name={`API.${idx}.WidgetSort.${sortIdx}.SortType`}
                                              data={[
                                                { value: 'asc' },
                                                { value: 'desc' }
                                              ]}
                                              dataTextField={"value"}
                                              dataValueField={"value"}
                                              label="Sort Type *"
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                                            <IconButton onClick={() => removeSort(sortIdx)}>
                                              <Delete />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      ))}
                                      <Grid container justifyContent="flex-start">
                                        <Button
                                          sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", mt: "15px", minWidth: 100, color: "#fff" }}
                                          onClick={() => pushSort({ SortField: '', SortType: '' })}
                                        >
                                          Add Sort Field
                                        </Button>
                                      </Grid>
                                    </>
                                  )}
                                </FieldArray>
                              </Grid>
                              {(selectedWidgetType === 'table') && (
                                <>
                                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ marginTop: "1%" }}>
                                    <Divider />
                                    <Typography variant="h5" sx={{ mt: "3px" }}>Widget Grouping</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                    <SelectField
                                      name={`API.${idx}.WidgetFieldGrouping[0].GroupByField`}
                                      data={APIResponseParams?.filter(x => x.webMethodKey === api.WebMethodKey) || []}
                                      dataTextField="parameterTagDisplayName"
                                      dataValueField="parameterTagDisplayName"
                                      label={`Group By Field ${values?.API?.[idx]?.WidgetFieldGrouping[0]?.GroupByField || values?.API?.[idx]?.WidgetFieldGrouping[0]?.GroupByAggField ? '*' : ''}`}
                                      onChange={(event) => {
                                        const selectedValue = event?.parameterTagDisplayName ?? null;
                                        setFieldValue(`API.${idx}.WidgetFieldGrouping[0].GroupByField`, selectedValue);
                                        if (!selectedValue) {
                                          setFieldValue(`API.${idx}.WidgetFieldGrouping[0].GroupByAggField`, '');
                                        }
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                    <SelectField
                                      name={`API.${idx}.WidgetFieldGrouping[0].GroupByAggField`}
                                      data={APIResponseParams?.filter(x => x.webMethodKey === api.WebMethodKey) || []}
                                      dataTextField="parameterTagDisplayName"
                                      dataValueField="parameterTagDisplayName"
                                      label={`Group By Aggregate Field ${values?.API?.[idx]?.WidgetFieldGrouping[0]?.GroupByField || values?.API?.[idx]?.WidgetFieldGrouping[0]?.GroupByAggField ? '*' : ''}`}
                                    />
                                  </Grid>
                                </>
                              )}
                              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Divider />
                                <Typography sx={{ marginTop: "1%" }} variant="h5">
                                  Filter Elements
                                </Typography>
                              </Grid>
                              <FieldArray name={`API.${idx}.WidgetFieldFilter`}>
                                {({ push: pushFilter, remove: removeFilter }) => (
                                  <>
                                    {api.WidgetFieldFilter?.map((filter, filterIdx) => (
                                      <Grid container spacing={2} key={filterIdx} sx={{ mt: "3px", ml: "2px" }}>
                                        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                                          <SelectField
                                            name={`API.${idx}.WidgetFieldFilter.${filterIdx}.FilterField`}
                                            data={APIResponseParams.filter(x => x.webMethodKey === api.WebMethodKey) || []}
                                            dataTextField={"parameterTagDisplayName"}
                                            dataValueField={"parameterTagDisplayName"}
                                            label="Filter Field *"
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                                          <InputField
                                            label="Filter Value *"
                                            name={`API.${idx}.WidgetFieldFilter.${filterIdx}.FilterValue`}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                                          <IconButton onClick={() => removeFilter(filterIdx)}>
                                            <Delete />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    ))}
                                    <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                      <Button
                                        sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100, color: "#fff" }}
                                        onClick={() => pushFilter({ FilterField: '', FilterValue: '' })}
                                      >
                                        Add Filter Field
                                      </Button>
                                    </Grid>
                                  </>
                                )}
                              </FieldArray>
                              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} >
                                <Divider />
                                <Typography sx={{ marginBottom: "2%", marginTop: "2%" }} variant="h5">
                                  Field Mapping
                                </Typography>
                                <Box>
                                  <FieldArray name={`API[${idx}].Fields`}>
                                    {({ push }) => (
                                      <>
                                        {api.Fields?.map((field, fieldIdx) => (
                                          <Grid container spacing={2} sx={{ marginBottom: 3 }} key={`field-${idx}-${fieldIdx}`}>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <InputField
                                                name={`API.${idx}.Fields.${fieldIdx}.FieldName`}
                                                label="Field Name *"
                                                InputLabelProps={{
                                                  shrink: !!values.API[idx].Fields[fieldIdx].FieldName,
                                                }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <InputField
                                                name={`API.${idx}.Fields.${fieldIdx}.DisplayName`}
                                                label="Display Name *"
                                                InputLabelProps={{
                                                  shrink: !!values.API[idx].Fields[fieldIdx].DisplayName,
                                                }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <SelectField
                                                name={`API.${idx}.Fields.${fieldIdx}.Type`}
                                                data={FieldTypeOptions}
                                                dataTextField="fieldType"
                                                dataValueField="fieldDataTypeMasterKey"
                                                label="Type *"
                                                onChange={(newValue) => handleFieldTypeChange(newValue, idx, fieldIdx)}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <SelectField
                                                name={`API.${idx}.Fields.${fieldIdx}.Format`}
                                                data={FieldFormatOptions.filter(x => x.fieldDataTypeMasterKey === api.Fields[fieldIdx]?.Type) || []}
                                                dataTextField={"format"}
                                                dataValueField={"formatMasterKey"}
                                                label="Format"
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <SelectField
                                                name={`API.${idx}.Fields.${fieldIdx}.APIResponse`}
                                                data={APIResponseParams?.filter(x => x.webMethodKey === api.WebMethodKey) || []}
                                                dataTextField="parameterTagDisplayName"
                                                dataValueField="webMethodsResponseParamKey"
                                                label="API Response *"
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={3} xl={2}>
                                              <InputField
                                                name={`API.${idx}.Fields.${fieldIdx}.MapField`}
                                                label="Map Field"
                                                InputLabelProps={{
                                                  shrink: !!values.API[idx].Fields[fieldIdx].MapField,
                                                }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2} lg={2} xl={1}>
                                              <InputField
                                                label="Sort Order"
                                                name={`API.${idx}.Fields.${fieldIdx}.SortOrder`}
                                                InputLabelProps={{
                                                  shrink: !!values.API[idx].Fields[fieldIdx].SortOrder,
                                                }}
                                              />
                                            </Grid>
                                            {(selectedWidgetType === 'keynumbers') && (
                                              <Grid item xs={12} sm={6} md={3} lg={3} xl={3} container spacing={2}>
                                                <Grid item xs={10}>
                                                  <SelectField
                                                    name={`API.${idx}.Fields.${fieldIdx}.Icon`}
                                                    data={iconOptions}
                                                    dataTextField="label"
                                                    dataValueField="value"
                                                    label="Icon *"
                                                    onChange={(newValue) => { handleIconChange(newValue, idx, fieldIdx) }}
                                                  />
                                                </Grid>
                                                <Grid item xs={2} sx={{ mt: "10px" }}>
                                                  {selectedIcons[`${idx}-${fieldIdx}`] && (
                                                    <FontAwesomeIcon icon={selectedIcons[`${idx}-${fieldIdx}`]} size="2x" />
                                                  )}
                                                </Grid>
                                              </Grid>
                                            )}
                                            <Grid item xs={12} sm={6} md={1} lg={4} xl={1}>
                                              <IconButton
                                                onClick={() => handleFieldDelete(idx, fieldIdx)}
                                              >
                                                <Delete />
                                              </IconButton>
                                            </Grid>
                                          </Grid>
                                        ))}
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12} display="flex" justifyContent="flex-end">
                                          <Button
                                            sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100, color: "#fff" }}
                                            onClick={() => {
                                              push({
                                                ...defaultField,
                                                SortOrder: api.Fields.length + 1
                                              })
                                            }}
                                          >
                                            Add Field
                                          </Button>
                                        </Grid>
                                      </>
                                    )}
                                  </FieldArray>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}
              </FieldArray>
            </Grid>
          </Box>
        </CardContent>
      </Card>
      <Dialog
        open={apiDialogOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setApiDialogOpen(false);
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #939ca3, #939ca3)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={() => setApiDialogOpen(false)}
          >
            No
          </Button>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #072f40, #072f40)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={handleDeleteApiConfirmation}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={fieldDialogOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setFieldDialogOpen(false);
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #939ca3, #939ca3)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={() => setFieldDialogOpen(false)}
          >
            No
          </Button>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #072f40, #072f40)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={handleDeleteFieldConfirmation}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};

export default APIConfigurationsCard;